import { TableRow } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

// COMPONENTS
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import KOGCollapse from '@/main/components/shared/kogCollapse/kogCollapse';
import {
  RowHeaderTextCenter,
  SkeletonLoading,
  SpinnerLoading
} from '@/main/components/shared/styles';

// SERVICES / REDUX
import {
  QuoteHeaderAnswerInformationResponse,
  QuoteSupplierItemPartResponse
} from '@/main/models/response';
import { StatusQuoteSupplierItem } from '@/main/services/enum/StatusQuoteSupplierItem';
import { getTranslateByKey } from '@/main/services/utils';
import { useWarehouseReducer } from '@/main/store/warehouse';

import KogCheckbox from '@/main/components/shared/checkbox/kogCheckbox';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ICreateAnswerForm } from '../interfaceCreateAnswer';
import TableRowAnswerGrouped from './TableRowAnswer/TableRowAnswerGrouped';
import { buildSizeColumns } from './columnConfig';
import { groupPendingItems } from './groupPendingItems';
import * as S from './styles';

interface ICardGridAnswerProps {
  dataGrid: QuoteSupplierItemPartResponse[];
  dataHeader?: QuoteHeaderAnswerInformationResponse;
  loading?: boolean;
  hasReadOnly?: boolean;
  idStatus: number;
  openModalOnce: boolean;
  isDeliveryBySupplier: boolean;
  isFinishedTime?: boolean;
  setOpenModalOnce: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalDetails: (guidPart: string) => void;
  updateDataGrid: (data: QuoteSupplierItemPartResponse[]) => void;
  isRequoteNational?: boolean;
  filterByFavorite?: boolean;
  cardTitle?: string;
  isOpenInitial?: boolean;
  isShowModalReasonAll: boolean;
  setIsShowModalReasonAll: React.Dispatch<React.SetStateAction<boolean>>;
  handleOpenModalImages: (item: QuoteSupplierItemPartResponse, allowEdit: boolean) => void;
}
const TitleHeader = ({
  title,
  notRequired = false,
  isTextCenter = false,
  subtitle = null
}: {
  title: string | React.ReactNode;
  notRequired?: boolean;
  isTextCenter?: boolean;
  subtitle?: string | null;
}) => (
  <S.RowTitleHeader>
    {isTextCenter ? (
      <RowHeaderTextCenter>
        {title}
        {!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
        <p>{subtitle}</p>
      </RowHeaderTextCenter>
    ) : (
      <S.ContainerTitle>
        <p>
          {title} {!notRequired && <S.TextAsterisk> *</S.TextAsterisk>}
        </p>
        <p>{subtitle}</p>
      </S.ContainerTitle>
    )}
  </S.RowTitleHeader>
);
function GridAnswer({
  dataGrid,
  dataHeader,
  loading = false,
  hasReadOnly = false,
  openModalOnce,
  isFinishedTime,
  setOpenModalOnce,
  isShowModalReasonAll,
  setIsShowModalReasonAll,
  handleOpenModalDetails,
  isDeliveryBySupplier = true,
  filterByFavorite,
  handleOpenModalImages
}: ICardGridAnswerProps) {
  const { control, setValue, getValues } = useFormContext<ICreateAnswerForm>();
  const { fields } = useFieldArray({ control, name: 'pendingItems' });

  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [havePartInventory, setHavePartInventory] = useState<boolean>(false);

  const { warehouse: warehouseReducer } = useWarehouseReducer();

  const haveOnlyOneWarehouse = warehouseReducer?.data?.length === 1;

  const watchedPendingItems = useWatch({ control, name: 'pendingItems' });
  const hasNotQuoteSelected = useMemo(
    () =>
      Array.isArray(watchedPendingItems) &&
      watchedPendingItems.some(item => item?.notQuote),
    [watchedPendingItems]
  );

  const sizeColumns = useMemo(
    () =>
      buildSizeColumns({
        haveOnlyOneWarehouse,
        isDeliveryBySupplier,
        havePartInventory,
        hasNotQuoteSelected
      }),
    [
      haveOnlyOneWarehouse,
      isDeliveryBySupplier,
      havePartInventory,
      hasNotQuoteSelected
    ]
  );

  const allGroups = useMemo(() => groupPendingItems(dataGrid), [dataGrid]);

  const groups = useMemo(() => {
    if (filterByFavorite === undefined) return allGroups;
    return allGroups.filter(group => {
      const isFavorite = group.rows[0]?.item.isFavorite ?? false;
      return filterByFavorite ? isFavorite : !isFavorite;
    });
  }, [allGroups, filterByFavorite]);

  function handleToggleSelectAll() {
    const pendingItems = getValues('pendingItems');

    if (allSelected) {
      pendingItems.forEach((item, index) => {
        if (item.notQuote) {
          setValue(`pendingItems.${index}.notQuote`, false);
          setValue(`pendingItems.${index}.reason`, null);
          setValue(`pendingItems.${index}.observation`, '');
        }
      });
      setAllSelected(false);
      return;
    }
    pendingItems.forEach((item, index) => {
      if (!item.notQuote && item.isPendingAnswer && !item.isNagsLoading) {
        setValue(`pendingItems.${index}.notQuote`, true);
      }
    });
    setAllSelected(true);
  }

  function getMaximumLengthHeader(string: string, maxchars: number): string {
    return string.length > maxchars + 1
      ? `${string.substring(0, maxchars)}.`
      : string;
  }

  const rowsHeader = [
    <TitleHeader title={getTranslateByKey('information.requestedPart')} />,
    <TitleHeader
      title={getTranslateByKey('information.partNumber')}
      notRequired
    />,
    <TitleHeader title={getTranslateByKey('information.partTypes')} />,
    !haveOnlyOneWarehouse && (
      <TitleHeader title={getTranslateByKey('information.warehouse')} />
    ),
    <TitleHeader title={getTranslateByKey('information.sku')} notRequired />,
    <TitleHeader title={getTranslateByKey('information.brand')} notRequired />,
    <TitleHeader title={getTranslateByKey('information.amount')} />,
    <TitleHeader title={getTranslateByKey('information.price')} />,
    <TitleHeader title={getTranslateByKey('information.pickupDaysDeadline')} />,
    !isDeliveryBySupplier && (
      <TitleHeader
        title={`${getTranslateByKey('partDetail.dimensions')}`}
        subtitle={`${getMaximumLengthHeader(
          getTranslateByKey('information.height'),
          4
        )} x ${getMaximumLengthHeader(
          getTranslateByKey('information.width'),
          4
        )} x ${getMaximumLengthHeader(
          getTranslateByKey('information.depth'),
          4
        )}`}
      />
    ),
    !isDeliveryBySupplier && (
      <TitleHeader title={`${getTranslateByKey('information.weight')} (kg)`} />
    ),
    <S.TitleHeaderCheckbox>
      {getTranslateByKey('information.notQuote')}
      {!hasReadOnly && (
        <KogCheckbox
          disabled={hasReadOnly}
          key="checkbox"
          checked={allSelected}
          value={allSelected}
          onChange={handleToggleSelectAll}
          id="id-checkbox-selectAll"
        />
      )}
    </S.TitleHeaderCheckbox>,
    havePartInventory ? <TitleHeader title="" notRequired /> : false
  ].filter(Boolean);

  function checkAllNotQuoteTrue(data: QuoteSupplierItemPartResponse[]) {
    return data.every(item => item.notQuote);
  }

  useEffect(() => {
    if (dataGrid && dataGrid.length != 0 && checkAllNotQuoteTrue(dataGrid)) {
      setAllSelected(true);
    }
  }, [dataGrid]);

  if (!loading && groups.length === 0) {
    return <ResultNotFoundComponent />;
  }

  return (
    <S.ContainerGrid>
      <BoxGrid
        rowHeaders={rowsHeader}
        typeGrid="backgroundBody"
        sizeColumns={sizeColumns}>
        {loading
          ? Array.from(new Array(4)).map((_, index) => (
            <TableRow key={index}>
              {rowsHeader.map((_, index) => (
                <S.TableCellGrid key={index} $showMobile>
                  <SkeletonLoading />
                </S.TableCellGrid>
              ))}
            </TableRow>
          ))
          : groups.map(group => (
            <TableRowAnswerGrouped
              key={group.groupKey}
              group={group}
              fieldIds={group.rows.map(
                ({ originalIndex }) => fields[originalIndex]?.id
              )}
              hasReadOnly={hasReadOnly}
              dataHeader={dataHeader}
              isFinishedTime={isFinishedTime}
              openModalOnce={openModalOnce}
              havePartInventory={havePartInventory}
              setOpenModalOnce={setOpenModalOnce}
              isShowModalReasonAll={isShowModalReasonAll}
              setIsShowModalReasonAll={setIsShowModalReasonAll}
              handleOpenModalDetails={handleOpenModalDetails}
              setAllSelected={setAllSelected}
              allSelected={allSelected}
              setHavePartInventory={setHavePartInventory}
              handleOpenModalImages={handleOpenModalImages}
            />
          ))}
      </BoxGrid>
    </S.ContainerGrid>
  );
}
function CardGridAnswerPending(props: ICardGridAnswerProps) {
  const title = props.cardTitle
    ? props.cardTitle
    : props.idStatus === StatusQuoteSupplierItem.Answered
      ? getTranslateByKey('information.partsAnswered')
      : getTranslateByKey('information.partsPending');

  return (
    <S.Container>
      <KOGCollapse
        isOpenCollapseinitial={props.isOpenInitial ?? true}
        showBorder
        keepMounted
        header={
          <S.AreaTitleCard>
            <S.TitleCard>{title}</S.TitleCard>
            {props.loading && <SpinnerLoading size="xsmall" noMargin />}
          </S.AreaTitleCard>
        }
        content={<GridAnswer {...props} />}
      />
    </S.Container>
  );
}

export default CardGridAnswerPending;
